import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  IconButton,
  Fab,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, PhotoCamera } from '@mui/icons-material';
import { supabase } from '../utils/supabase';
import IngredientForm from '../components/Pantry/IngredientForm';
import ReceiptScanner from '../components/Pantry/ReceiptScanner';

const CATEGORIES = [
  'Vegetables',
  'Fruits',
  'Meat',
  'Dairy',
  'Grains',
  'Legumes',
  'Herbs & Spices',
  'Beverages',
  'Condiments',
  'Snacks',
  'Frozen',
  'Canned',
  'Other'
];

const EXPIRY_STATUSES = [
  { value: 'expired', label: 'Expired' },
  { value: 'urgent', label: 'Expires Soon (≤3 days)' },
  { value: 'warning', label: 'Expiring (≤7 days)' },
  { value: 'good', label: 'Fresh (>7 days)' },
  { value: 'none', label: 'No expiry date' }
];

export default function PantryPage({ session }) {
  
  const navigate = useNavigate();
  
  const [ingredients, setIngredients] = useState([]);
  const [filteredIngredients, setFilteredIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [receiptScannerOpen, setReceiptScannerOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Filter and sort states
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('expiry_date');

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry date';
    return new Date(dateString).toLocaleDateString();
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return 'none';
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 3) return 'urgent';
    if (daysUntilExpiry <= 7) return 'warning';
    return 'good';
  };

  const getExpiryChip = (expiryDate) => {
    const status = getExpiryStatus(expiryDate);
    switch (status) {
      case 'expired':
        return <Chip label="Expired" color="error" size="small" />;
      case 'urgent':
        return <Chip label="Expires Soon" color="error" size="small" />;
      case 'warning':
        return <Chip label="Expiring" color="warning" size="small" />;
      case 'good':
        return <Chip label="Fresh" color="success" size="small" />;
      default:
        return null;
    }
  };

  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...ingredients];

    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter(ingredient => ingredient.category === categoryFilter);
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(ingredient => getExpiryStatus(ingredient.expiry_date) === statusFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'expiry_date') {
        const dateA = a.expiry_date ? new Date(a.expiry_date) : new Date('9999-12-31');
        const dateB = b.expiry_date ? new Date(b.expiry_date) : new Date('9999-12-31');
        return dateA - dateB;
      } else if (sortBy === 'quantity') {
        return parseFloat(b.quantity) - parseFloat(a.quantity); // Descending order
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });

    setFilteredIngredients(filtered);
  }, [ingredients, categoryFilter, statusFilter, sortBy]);

  const fetchIngredients = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!session?.user?.id) {
        setError('You must be logged in to view ingredients');
        setLoading(false);
        return;
      }
      
      // Use direct REST API call to avoid session hanging issues
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/ingredients?user_id=eq.${session.user.id}&order=expiry_date.asc.nullslast`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      setIngredients(data || []);
    } catch (error) {
      console.error('🥫 Error fetching ingredients:', error);
      setError('Failed to fetch ingredients: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchIngredients();
  }, [session, fetchIngredients]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [applyFiltersAndSort]);

  const resetFilters = () => {
    setCategoryFilter('');
    setStatusFilter('');
    setSortBy('expiry_date');
  };

  const handleAddIngredient = () => {
    setEditingIngredient(null);
    setOpenDialog(true);
  };

  const handleEditIngredient = (ingredient) => {
    setEditingIngredient(ingredient);
    setOpenDialog(true);
  };

  const handleDeleteIngredient = async (id) => {
    try {
      // Use direct REST API call to avoid session hanging issues
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/ingredients?id=eq.${id}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      await fetchIngredients(); // Refresh the list
    } catch (error) {
      console.error('Error deleting ingredient:', error);
      setError('Failed to delete ingredient: ' + error.message);
    }
  };

  const handleIngredientSaved = async () => {
    setOpenDialog(false);
    setEditingIngredient(null);
    await fetchIngredients(); // Refresh the list
  };

  // Handler for receipt scanner extracted ingredients
  const handleIngredientsExtracted = async (extractedItems) => {
    try {
      setError(null);
      
      if (!session?.user) {
        setError('You must be logged in to add ingredients');
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const item of extractedItems) {
        try {
          const ingredientData = {
            name: item.name,
            quantity: parseFloat(item.quantity) || 1,
            unit: item.unit || 'pieces',
            category: item.category || 'Other',
            expiry_date: item.expiry_date || null,
            notes: `Added from receipt scan (confidence: ${item.confidence})`,
            user_id: session.user.id
          };

          // Use direct REST API call to avoid session hanging issues
          const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/ingredients`;
          
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
              'Prefer': 'return=representation'
            },
            body: JSON.stringify(ingredientData)
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          successCount++;
        } catch (itemError) {
          console.error(`Error adding ingredient ${item.name}:`, itemError);
          errorCount++;
        }
      }

      if (successCount > 0) {
        setSuccessMessage(`Successfully added ${successCount} items from receipt!`);
        await fetchIngredients(); // Refresh the list
      }

      if (errorCount > 0) {
        setError(`Failed to add ${errorCount} items. Please check for duplicates or try adding them manually.`);
      }

    } catch (error) {
      console.error('Error processing extracted ingredients:', error);
      setError('Failed to add ingredients from receipt');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            My Pantry
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* Filter and Sort Controls */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Filter & Sort
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
                autoWidth
                sx={{ 
                  minHeight: 56,
                  '& .MuiSelect-select': {
                    minWidth: categoryFilter ? 'auto' : 120
                  }
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                autoWidth
                sx={{ 
                  minHeight: 56,
                  '& .MuiSelect-select': {
                    minWidth: statusFilter ? 'auto' : 120
                  }
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                {EXPIRY_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
                autoWidth
                sx={{ 
                  minHeight: 56,
                  '& .MuiSelect-select': {
                    minWidth: 120
                  }
                }}
              >
                <MenuItem value="expiry_date">Expiry Date</MenuItem>
                <MenuItem value="quantity">Quantity</MenuItem>
                <MenuItem value="name">Name</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              onClick={resetFilters}
              sx={{ height: 56, minWidth: 120 }}
            >
              Reset Filters
            </Button>
          </Box>
        </Paper>

        {filteredIngredients.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {ingredients.length === 0 ? 'Your pantry is empty!' : 'No ingredients match your filters.'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {ingredients.length === 0 ? 'Add some ingredients to get started.' : 'Try adjusting your filters or reset them.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddIngredient}
              size="large"
            >
              {ingredients.length === 0 ? 'Add Your First Ingredient' : 'Add New Ingredient'}
            </Button>
          </Box>
        ) : (
          <>
            {/* Results Summary */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredIngredients.length} of {ingredients.length} ingredients
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  onClick={() => setReceiptScannerOpen(true)}
                  size="small"
                >
                  Scan Receipt
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddIngredient}
                  size="small"
                >
                  Add Ingredient
                </Button>
              </Box>
            </Box>

            {/* Mobile Card View */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {filteredIngredients.map((ingredient) => (
                <Card key={ingredient.id} sx={{ mb: 2 }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, flex: 1 }}>
                        {ingredient.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditIngredient(ingredient)}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteIngredient(ingredient.id)}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Chip
                        label={`${ingredient.quantity} ${ingredient.unit}`}
                        variant="outlined"
                        size="small"
                        color="primary"
                      />
                      {ingredient.category && (
                        <Chip
                          label={ingredient.category}
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {getExpiryChip(ingredient.expiry_date)}
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Expires: {formatDate(ingredient.expiry_date)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Desktop Table View */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Expiry Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredIngredients.map((ingredient) => (
                      <TableRow key={ingredient.id}>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {ingredient.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {ingredient.quantity} {ingredient.unit}
                        </TableCell>
                        <TableCell>
                          {ingredient.category && (
                            <Chip
                              label={ingredient.category}
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatDate(ingredient.expiry_date)}</TableCell>
                        <TableCell>{getExpiryChip(ingredient.expiry_date)}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditIngredient(ingredient)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteIngredient(ingredient.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            
            <Fab
              color="primary"
              aria-label="add"
              onClick={handleAddIngredient}
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
              }}
            >
              <AddIcon />
            </Fab>
          </>
        )}

        <IngredientForm
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSave={handleIngredientSaved}
          ingredient={editingIngredient}
          session={session}
        />

        <ReceiptScanner
          open={receiptScannerOpen}
          onClose={() => setReceiptScannerOpen(false)}
          onIngredientsExtracted={handleIngredientsExtracted}
        />
      </Box>
    </Container>
  );
}
